"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useGameState = void 0;
const react_1 = require("react");
const values_1 = __importDefault(require("../values"));
const lodash_1 = __importDefault(require("lodash"));
const smap_1 = require("../smap");
const useLocalStorage_1 = require("./useLocalStorage");
const State_1 = require("../typeDefs/State");
const assembly_1 = require("../assembly");
const bigmath_1 = require("../bigmath");
const numberFormatter_1 = require("../numberFormatter");
const stateRef = {
    current: null,
};
function useGameState() {
    const { existingStorage, saveGame, resetGame } = (0, useLocalStorage_1.useLocalStorage)();
    if (!stateRef.current) {
        stateRef.current = existingStorage;
    }
    const makeByHandTimeRef = (0, react_1.useRef)(0);
    const setState = (state = {}) => {
        stateRef.current = { ...stateRef.current, ...state };
    };
    const markVisibility = (0, react_1.useCallback)((item, b) => {
        stateRef.current.visible[item] = b;
        stateRef.current.acknowledged[item] ??= false;
    }, []);
    const disableRecipe = (0, react_1.useCallback)((itemName, disable) => {
        stateRef.current.disabledRecipes[itemName] = disable;
    }, []);
    const acknowledgeItem = (item) => {
        stateRef.current.acknowledged[item] = true;
    };
    const resetAll = (0, react_1.useCallback)(() => {
        setState(resetGame());
    }, []);
    function calculateStorage(itemName) {
        const canBeStoredIn = values_1.default.itemsCanBeStoreIn[itemName];
        if (canBeStoredIn.length === 0)
            return bigmath_1.REALLY_BIG;
        const storage = stateRef.current.storage[itemName];
        if (storage === undefined)
            return values_1.default.MIN_STORAGE;
        const assemblers = stateRef.current.assemblers[itemName] ?? {};
        return ((0, bigmath_1.bigMax)((0, bigmath_1.bigSum)((0, smap_1.keys)(assemblers).map((key) => (0, bigmath_1.bigMul)(assemblers[key] ?? 0n, values_1.default.storageSizes[key]))), 0n) +
            (0, bigmath_1.bigMax)((0, bigmath_1.bigSum)((0, smap_1.keys)(storage).map((key) => (0, bigmath_1.bigMul)(storage[key] ?? 0n, canBeStoredIn.includes(key)
                ? values_1.default.storageSizes[key] ?? 0n
                : 0n))), 0n) +
            values_1.default.MIN_STORAGE);
    }
    function hasStorageCapacity(item) {
        const currentAmount = stateRef.current.amountThatWeHave[item] ?? 0n;
        return calculateStorage(item) - currentAmount;
    }
    function addToTotal(itemName, recipeCount) {
        if (values_1.default.sideProducts[itemName].length > 0) {
            const itemsChosen = {};
            values_1.default.sideProducts[itemName].forEach((sideProduct) => {
                const total = lodash_1.default.sum((0, smap_1.values)(sideProduct));
                let runningTotal = 0;
                lodash_1.default.forIn((0, smap_1.keys)(sideProduct), (key) => {
                    const k = key;
                    runningTotal += sideProduct[k] ?? 0;
                    if (Math.random() <= runningTotal / total) {
                        itemsChosen[k] = hasStorageCapacity(k);
                        return false;
                    }
                });
            });
            const canProduce = (0, bigmath_1.bigMin)(recipeCount, ...Object.values(itemsChosen));
            if ((0, bigmath_1.bigGtE)(canProduce, 1)) {
                (0, smap_1.keys)(itemsChosen).forEach((x) => addAmount(x, canProduce));
            }
            else {
                return false;
            }
            return true;
        }
        else {
            const capacity = (0, bigmath_1.bigMin)(recipeCount, hasStorageCapacity(itemName));
            if ((0, bigmath_1.bigGtE)(capacity, 1)) {
                addAmount(itemName, capacity);
                return true;
            }
        }
        return false;
    }
    function getPower(itemName, building) {
        const power = ((stateRef.current.powerConsumptionProgress[itemName] ??=
            {})[building] ??= 0);
        const state = ((stateRef.current.powerConsumptionState[itemName] ??=
            {})[building] ??= State_1.PRODUCTION_NO_POWER);
        return [power, state];
    }
    function getProductionProgress(itemName, assemblerName) {
        const time = ((stateRef.current.productionProgress[itemName] ??= {})[assemblerName] ??= 0n);
        const state = ((stateRef.current.productionState[itemName] ??= {})[assemblerName] ??= State_1.PRODUCTION_NO_INPUT);
        return [time, state];
    }
    const addAmount = (0, react_1.useCallback)((itemName, amount) => {
        const k = stateRef.current.amountThatWeHave[itemName] ?? 0n;
        stateRef.current.amountThatWeHave[itemName] = (0, bigmath_1.bigMax)(0n, k + amount);
        const b = stateRef.current.amountCreated[itemName] ?? 0n;
        stateRef.current.amountCreated[itemName] = b + amount;
        if (values_1.default.hideOnBuy(itemName)) {
            markVisibility(itemName, false);
            stateRef.current.assemblers[itemName] = {};
        }
    }, []);
    const makeItemByhand = (0, react_1.useCallback)((itemName, count) => {
        const now = new Date().getTime();
        if (makeByHandTimeRef.current > now - 200)
            return;
        makeByHandTimeRef.current = now;
        if ((0, assembly_1.consumeMaterialsFromRecipe)(itemName, stateRef.current.amountThatWeHave, count)) {
            addToTotal(itemName, count);
        }
    }, []);
    function dispatchAction(action) {
        switch (action.action) {
            case 'add-box': {
                addContainer(action.recipe, action.box, action.amount);
                break;
            }
            case 'add-building': {
                addAssemblers(action.building, action.recipe, action.amount);
                break;
            }
            case 'craft-byhand': {
                makeItemByhand(action.recipe, action.amount);
                break;
            }
            case 'hide-item': {
                if (!stateRef.current.visible[action.itemName])
                    return;
                stateRef.current.visible[action.itemName] = false;
                break;
            }
            case 'unhide-item': {
                if (stateRef.current.visible[action.itemName])
                    return;
                const { visible, acknowledged, timeSpentPlaying, timeUnlockedAt, } = stateRef.current;
                visible[action.itemName] = true;
                acknowledged[action.itemName] ??= !values_1.default.displayNewBadge[action.itemName];
                timeUnlockedAt[action.itemName] ??= timeSpentPlaying;
                break;
            }
            case 'remove-building': {
                addAssemblers(action.building, action.recipe, -action.amount);
                break;
            }
            case 'ack': {
                acknowledgeItem(action.recipe);
                break;
            }
            case 'disable-recipe': {
                disableRecipe(action.recipe, true);
                break;
            }
            case 'enable-recipe': {
                disableRecipe(action.recipe, false);
                break;
            }
            case 'reset-game': {
                resetAll();
                break;
            }
            case 'set-amount': {
                setAmount((0, numberFormatter_1.parseFormat)(action.amount), action.item);
                break;
            }
            case 'add-amount': {
                addAmount(action.item, (0, numberFormatter_1.parseFormat)(action.amount));
                break;
            }
            case 'hide-building-add-button': {
                if (stateRef.current.hideAddButtons[action.building])
                    return;
                stateRef.current.hideAddButtons[action.building] = true;
                break;
            }
            case 'unhide-building-add-button': {
                if (!stateRef.current.hideAddButtons[action.building])
                    return;
                stateRef.current.hideAddButtons[action.building] = false;
                break;
            }
            default: {
                const _EXHAUSTIVE = action;
            }
        }
    }
    const setAmount = (amount = 1n, itemName = "") => {
        stateRef.current.amountThatWeHave[itemName] = amount;
        stateRef.current.visible[itemName] ??= true;
    };
    const addAssemblers = (0, react_1.useCallback)((level, itemName, amount) => {
        const k = stateRef.current.assemblers[itemName] ?? {};
        const appliedAssemblers = k[level] ?? 0n;
        const haveAssemblers = stateRef.current.amountThatWeHave[level] ?? 0n;
        amount = (0, bigmath_1.bigMin)(amount, haveAssemblers);
        k[level] = (0, bigmath_1.bigMax)(0n, appliedAssemblers + amount);
        stateRef.current.assemblers[itemName] = k;
        stateRef.current.amountThatWeHave[level] = haveAssemblers - amount;
    }, []);
    const addContainer = (0, react_1.useCallback)((itemName, container, amount) => {
        stateRef.current.storage[itemName] ??= {};
        const haveStorage = stateRef.current.storage[itemName][container] ?? 0n;
        const haveContainers = stateRef.current.amountThatWeHave[container] ?? 0n;
        amount = (0, bigmath_1.bigMin)(amount, haveContainers);
        stateRef.current.storage[itemName][container] =
            haveStorage + amount;
        stateRef.current.amountThatWeHave[container] =
            haveContainers - amount;
    }, []);
    return {
        stateRef,
        addAmount,
        addAssemblers,
        addContainer,
        addToTotal,
        setAmount,
        dispatchAction,
        makeItemByhand,
        getPower,
        getProductionProgress,
        markVisibility,
        hasStorageCapacity,
        saveGame,
        calculateStorage,
    };
}
exports.useGameState = useGameState;
